import type { MouseEvent } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import handDrawnArrow from '@backrs_ui/grow-ui/src/assets/images/success-system/hand-drawn-arrow.svg';
import {
  SUCCESS_SYSTEM_SECTION_IDS,
  successSystemContent,
  type SuccessSystemSectionId,
} from '@backrs_ui/grow-ui/src/pages/SuccessSystem/successSystem.content';
import { revealElement } from '@backrs_ui/grow-ui/src/utils/scrollReveal';

import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

type StyleFn = (theme: Theme) => SystemStyleObject<Theme>;

type AppIconName = React.ComponentProps<typeof AppIcon>['iconName'];

type DiagramItem = Readonly<{
  label: string;
  icon: AppIconName;
  target?: SuccessSystemSectionId;
}>;

type DiagramGroup = Readonly<{
  key: string;
  title: string;
  target: SuccessSystemSectionId;
  items: readonly DiagramItem[];
}>;

const IDS = SUCCESS_SYSTEM_SECTION_IDS;

const ARROW_HINT = successSystemContent.overview.diagramHint;

const TopArrow: React.FC<Readonly<{ id: string; mirrored?: boolean }>> = ({ id, mirrored }) => (
  <Tooltip title={ARROW_HINT} placement="top" arrow>
    <Box
      id={id}
      component="img"
      src={handDrawnArrow}
      alt=""
      sx={[arrowSx, !!mirrored && arrowMirroredSx]}
    />
  </Tooltip>
);

const BottomArrow: React.FC<Readonly<{ id: string; mirrored?: boolean }>> = ({ id, mirrored }) => (
  <Tooltip title={ARROW_HINT} placement="top" arrow>
    <Box
      id={id}
      component="svg"
      viewBox="0 0 88 96"
      fill="none"
      aria-hidden
      sx={[bottomArrowSx, !!mirrored && arrowMirroredSx]}
    >
      <path d="M10 2 C 2 44, 30 80, 82 86" />
      <path d="M70 76 L 82 86 L 67 93" />
    </Box>
  </Tooltip>
);

const DIAGRAM_GROUPS: readonly DiagramGroup[] = [
  {
    key: 'fundamentals',
    title: successSystemContent.fundamentals.group.title,
    target: IDS.fundamentals,
    items: [
      { label: 'Four Unlocks', icon: 'Lock01', target: IDS.fourUnlocks },
      { label: 'Strengths/Opportunities', icon: 'Target04', target: IDS.knowYourStrengths },
      { label: 'Hinge Moments', icon: 'Clock' },
    ],
  },
  {
    key: 'big-decisions',
    title: successSystemContent.bigDecisions.group.title,
    target: IDS.bigDecisions,
    items: [
      { label: 'Career Tracks', icon: 'Route', target: IDS.careerTracks },
      { label: 'Education Investments', icon: 'GraduationHat02', target: IDS.educationInvestments },
      { label: 'Career Paths', icon: 'Briefcase01', target: IDS.careerPaths },
    ],
  },
  {
    key: 'track-progression',
    title: successSystemContent.trackProgression.group.title,
    target: IDS.trackProgression,
    items: [
      { label: 'Invest where it counts', icon: 'File02', target: IDS.investWhereItCounts },
      { label: 'Calendar of Milestones', icon: 'Calendar', target: IDS.calendarOfMilestones },
    ],
  },
];

const toSlug = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

type AnchorProps = Readonly<{
  id: string;
  target: SuccessSystemSectionId;
  sx: SxProps<Theme>;
  children: React.ReactNode;
}>;

const SectionAnchor: React.FC<AnchorProps> = ({ id, target, sx, children }) => (
  <Box
    component="a"
    id={id}
    href={`#${target}`}
    sx={sx}
    onClick={(event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      revealElement(document.getElementById(target), 'start');
    }}
  >
    {children}
  </Box>
);

const DiagramItemRow: React.FC<Readonly<{ item: DiagramItem }>> = ({ item }) => {
  const content = (
    <>
      <AppIcon iconName={item.icon} diameter="16px" color="currentColor" />
      <Typography variant="text-sm-regular" component="span">
        {item.label}
      </Typography>
    </>
  );

  if (!item.target) {
    return <Box sx={itemSx}>{content}</Box>;
  }

  return (
    <SectionAnchor
      id={`success-system-diagram-${toSlug(item.label)}-link`}
      target={item.target}
      sx={[itemSx, linkSx]}
    >
      {content}
    </SectionAnchor>
  );
};

const DiagramGroupCard: React.FC<Readonly<{ group: DiagramGroup }>> = ({ group }) => (
  <Box sx={groupCardSx}>
    <SectionAnchor
      id={`success-system-diagram-${group.key}-link`}
      target={group.target}
      sx={[groupTitleSx, linkSx]}
    >
      {group.title}
    </SectionAnchor>
    <Box sx={itemListSx}>
      {group.items.map((item) => (
        <DiagramItemRow key={item.label} item={item} />
      ))}
    </Box>
  </Box>
);

export const SuccessSystemDiagram: React.FC = () => {
  const [fundamentals, bigDecisions, trackProgression] = DIAGRAM_GROUPS;

  return (
    <Box id="success-system-diagram" sx={diagramSx}>
      <Box sx={topRowSx}>
        <TopArrow id="success-system-diagram-left-top-arrow" />
        <SectionAnchor
          id="success-system-diagram-life-aspirations-link"
          target={IDS.lifeAspirations}
          sx={[lifeAspirationsSx, linkSx]}
        >
          <AppIcon iconName="UserCircle" diameter="24px" color="currentColor" />
          {successSystemContent.lifeAspirations.group.title}
        </SectionAnchor>
        <TopArrow id="success-system-diagram-right-top-arrow" mirrored />
      </Box>
      <Box sx={groupRowSx}>
        <DiagramGroupCard group={fundamentals} />
        <DiagramGroupCard group={bigDecisions} />
      </Box>
      <Box sx={bottomRowSx}>
        <BottomArrow id="success-system-diagram-left-bottom-arrow" />
        <DiagramGroupCard group={trackProgression} />
        <BottomArrow id="success-system-diagram-right-bottom-arrow" mirrored />
      </Box>
    </Box>
  );
};

const diagramSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
  containerType: 'inline-size',
});

const topRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-3xl'],
});

const bottomRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-md'],
});

// Hardcoded: the bottom row needs 411px for both arrows beside its card; narrower maps drop the arrows.
const hideWhenNarrowSx: SystemStyleObject<Theme> = {
  '@container (max-width: 410px)': { display: 'none' },
};

// Hardcoded: native size of the hand-drawn arrow artwork.
const arrowSx: SystemStyleObject<Theme> = {
  ...hideWhenNarrowSx,
  width: '55px',
  height: '32px',
  flexShrink: 0,
};

// Hardcoded: native size of the drawn sweep path's viewBox.
const bottomArrowSx: StyleFn = ({ backrs }) => ({
  ...hideWhenNarrowSx,
  width: '88px',
  height: '96px',
  flexShrink: 0,
  stroke: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

const arrowMirroredSx: SystemStyleObject<Theme> = {
  transform: 'scaleX(-1)',
};

const lifeAspirationsSx: StyleFn = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-3xl'],
  py: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const groupRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

// Hardcoded: fixed card width keeps the map's rows aligned.
const groupCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '219px',
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const groupTitleSx: StyleFn = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const itemListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const itemSx: StyleFn = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const linkSx: StyleFn = ({ backrs }) => ({
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover, &:focus-visible': {
    color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
});
